import React from 'react';
import './Toast.scss';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';

const SuccessCircleIcon: React.FC = () => (
  <>
    <svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="2.5" y="2.5" width="33" height="33" rx="16.5" stroke="#C7EAD8" strokeWidth="5" />
      <path
        d="M19 6.13379C20.77 6.13383 22.4493 6.47128 24.0254 7.15137C25.5781 7.82143 26.941 8.7369 28.1025 9.89844C29.264 11.06 30.1796 12.4229 30.8496 13.9756C31.5296 15.5516 31.8662 17.2311 31.8662 19.001C31.8662 20.7709 31.5297 22.4503 30.8496 24.0264C30.1795 25.5792 29.2641 26.9419 28.1025 28.1035C26.9409 29.2651 25.5782 30.1805 24.0254 30.8506C22.4493 31.5307 20.77 31.8671 19 31.8672C17.2301 31.8672 15.5507 31.5306 13.9746 30.8506C12.4219 30.1806 11.059 29.265 9.89746 28.1035C8.73592 26.942 7.82045 25.5791 7.15039 24.0264C6.47031 22.4503 6.13285 20.7709 6.13281 19.001C6.13281 17.231 6.47031 15.5517 7.15039 13.9756C7.82047 12.4227 8.73583 11.0601 9.89746 9.89844C11.0591 8.7368 12.4217 7.82144 13.9746 7.15137C15.5507 6.47128 17.23 6.13379 19 6.13379Z"
        fill="white"
        stroke="#379564"
        strokeWidth="2.4"
      />
    </svg>
    <CheckIcon className="str-toast__success-check" />
  </>
);

export type ToastVariant = 'success' | 'error' | 'warning' | 'info' | 'generic';

export type ToastPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

export interface ToastItemData {
  id: string;
  content: React.ReactNode;
  variant: ToastVariant;
  autoClose: number | false;
  position?: ToastPosition;
}

interface VariantIconProps {
  variant: ToastVariant;
}

const VariantIcon: React.FC<VariantIconProps> = ({ variant }) => {
  switch (variant) {
    case 'success':
      return <SuccessCircleIcon />;
    case 'error':
      return <ErrorIcon className="str-toast__icon-svg" />;
    case 'warning':
      return <WarningIcon className="str-toast__icon-svg" />;
    case 'info':
      return <InfoIcon className="str-toast__icon-svg" />;
    default:
      return null;
  }
};

export interface ToastProps {
  item: ToastItemData;
  onDismiss: (id: string) => void;
}

export const Toast: React.FC<ToastProps> = ({ item, onDismiss }) => {
  const { id, content, variant } = item;

  return (
    <div className={`str-toast str-toast--${variant}`} role="alert" aria-live="polite">
      {variant !== 'generic' && (
        <span className="str-toast__icon">
          <VariantIcon variant={variant} />
        </span>
      )}
      <div className="str-toast__content">{content}</div>
      <button
        className="str-toast__close"
        onClick={() => onDismiss(id)}
        aria-label="Dismiss notification"
        type="button"
      >
        <CloseIcon className="str-toast__close-icon" />
      </button>
    </div>
  );
};

export interface ToastListContainerProps {
  items: ToastItemData[];
  position: ToastPosition;
  onDismiss: (id: string) => void;
}

export const ToastListContainer: React.FC<ToastListContainerProps> = ({
  items,
  position,
  onDismiss,
}) => {
  if (items.length === 0) return null;
  return (
    <div className={`str-toast-container str-toast-container--${position}`}>
      {items.map((item) => (
        <Toast key={item.id} item={item} onDismiss={onDismiss} />
      ))}
    </div>
  );
};
